document.addEventListener('DOMContentLoaded', () => {
  const JDGM_REVIEW_WIDGET_ROOT = '.jdgm-widget.jdgm-review-widget';
  const JDGM_SUBTAB_SELECTOR = '.jdgm-subtab';

  const SUBTAB_CLEANUP_DELAYS_MS = [0, 600, 2000];
  const SUBTAB_OBSERVER_MAX_OBSERVATIONS = 80;
  const SUBTAB_OBSERVER_DEBOUNCE_MS = 200;
  const SUBTAB_OBSERVER_TIMEOUT_MS = 12000;

  /**
   * Judge.me sometimes injects duplicate .jdgm-subtab rows (e.g. double Reviews/Q&A tabs).
   * Within each review widget, remove later subtabs whose label matches an earlier one.
   * Returns true if any duplicate node was removed (used to stop the observer early when scoped).
   */
  function removeDuplicateJdgmSubtabs(root = document) {
    let removedAny = false;
    const widgets = root.querySelectorAll(JDGM_REVIEW_WIDGET_ROOT);
    widgets.forEach((widget) => {
      const subtabs = widget.querySelectorAll(JDGM_SUBTAB_SELECTOR);
      if (subtabs.length < 2) return;

      const seen = new Set();
      subtabs.forEach((subtab) => {
        const key = subtab.textContent.replace(/\s+/g, ' ').trim();
        if (!key) return;
        if (seen.has(key)) {
          subtab.remove();
          removedAny = true;
        } else {
          seen.add(key);
        }
      });
    });
    return removedAny;
  }

  SUBTAB_CLEANUP_DELAYS_MS.forEach((ms) => setTimeout(() => removeDuplicateJdgmSubtabs(), ms));

  if ('MutationObserver' in window) {
    let debounceId;
    let observations = 0;
    let observingBody = false;

    /**
     * While the review widget is not in the DOM yet, observe document.body so we notice when
     * Judge.me mounts. As soon as widget root(s) exist, re-scope to those nodes only to avoid
     * reacting to every mutation on the page (carousels, analytics, etc.).
     */
    const scopeObserverToWidgetsIfPresent = () => {
      if (!observingBody) return;
      const widgets = document.querySelectorAll(JDGM_REVIEW_WIDGET_ROOT);
      if (widgets.length === 0) return;
      observer.disconnect();
      observingBody = false;
      widgets.forEach((el) => observer.observe(el, { childList: true, subtree: true }));
    };

    const observer = new MutationObserver(() => {
      scopeObserverToWidgetsIfPresent();

      observations += 1;
      if (observations > SUBTAB_OBSERVER_MAX_OBSERVATIONS) {
        observer.disconnect();
        return;
      }
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        const removedDuplicates = removeDuplicateJdgmSubtabs();
        if (removedDuplicates && !observingBody) {
          observer.disconnect();
          clearTimeout(debounceId);
          clearTimeout(observerLifetimeId);
        }
      }, SUBTAB_OBSERVER_DEBOUNCE_MS);
    });

    const widgetTargets = document.querySelectorAll(JDGM_REVIEW_WIDGET_ROOT);
    if (widgetTargets.length > 0) {
      widgetTargets.forEach((el) => observer.observe(el, { childList: true, subtree: true }));
    } else {
      observingBody = true;
      observer.observe(document.body, { childList: true, subtree: true });
    }

    const observerLifetimeId = setTimeout(() => {
      observer.disconnect();
      clearTimeout(debounceId);
    }, SUBTAB_OBSERVER_TIMEOUT_MS);
  }

  const JudgMeReviews = (() => {
    const {
      apiUrl,
      apiToken,
      shopDomain,
      productHandle,
      isProduction,
    } = window.judgmeReviewData || {};

    const PRODUCT_ID = '-1';
    const REVIEWS_PER_PAGE = 5;
    const MAX_RETRIES = 3;

    const Selectors = {
      REVIEW_WIDGET: '.jdgm-rev-widg',
      REVIEW_ELEMENTS: '.jdgm-rev-widg__reviews .jdgm-rev',
      REVIEW_TIMESTAMP: '.jdgm-rev__timestamp',
      REVIEW_AUTHOR: '.jdgm-rev__author',
      REVIEW_BODY: '.jdgm-rev__body',
      REVIEW_TITLE: '.jdgm-rev__title',
      REVIEW_RATING: '.jdgm-rev__rating',
      CUSTOM_MARKUP: '#custom-markup',
    };

    const zeroPad = (num) => num.toString().padStart(2, '0');
    const formatDate = (inputDate, format) => {
      const date = new Date(inputDate);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      const year = date.getFullYear().toString();
      const month = zeroPad(date.getMonth() + 1);
      const day = zeroPad(date.getDate());

      let formatted = format.replace('MM', month).replace('dd', day);

      if (format.includes('yyyy')) {
        formatted = formatted.replace('yyyy', year);
      } else if (format.includes('yy')) {
        formatted = formatted.replace('yy', year.substring(2, 4));
      }
      return formatted;
    };

    const parseRatingScore = (ratingElement) => {
      if (!ratingElement) return null;
      const fromDataset = (el) => {
        const raw = el.dataset?.score ?? el.getAttribute?.('data-score');
        if (raw == null || raw === '') return null;
        const n = parseFloat(String(raw).trim());
        return Number.isFinite(n) ? n : null;
      };
      let score = fromDataset(ratingElement);
      if (score == null) {
        const nested = ratingElement.querySelector('[data-score]');
        if (nested) score = fromDataset(nested);
      }
      if (score == null || score < 1 || score > 5) return null;
      return score;
    };

    const fetchProductReviews = async (attempt = 1) => {
      if (!apiToken) return null;
      const url = `${apiUrl}?` +
        `api_token=${encodeURIComponent(apiToken)}` +
        `&shop_domain=${encodeURIComponent(shopDomain)}` +
        `&id=${encodeURIComponent(PRODUCT_ID)}` +
        `&handle=${encodeURIComponent(productHandle)}` +
        `&per_page=${REVIEWS_PER_PAGE}`;
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        return parseReviews(result.widget);
      } catch (error) {
        if (attempt < MAX_RETRIES) {
          return fetchProductReviews(attempt + 1);
        }
        if (!isProduction) {
          console.error(`Error fetching product reviews after ${MAX_RETRIES} attempts:`, error);
        }
        return null;
      }
    };

    const parseReviews = (widgetHtml) => {
      if (!widgetHtml) return null;
      const parser = new DOMParser();
      const doc = parser.parseFromString(widgetHtml, 'text/html');
      const reviewElements = doc.querySelectorAll(Selectors.REVIEW_ELEMENTS);
      const ratingWidget = doc.querySelector(Selectors.REVIEW_WIDGET);
      if (!ratingWidget) return null;
      const rating = aggregateRating(ratingWidget);
      return {
        review: Array.from(reviewElements).map((element) => parseReview(element)),
        aggregateRating: rating,
      };
    };

    const parseReview = (element) => {
      const getDatePublished = element.querySelector(Selectors.REVIEW_TIMESTAMP)?.dataset.content;
      const getAuthorName = element.querySelector(Selectors.REVIEW_AUTHOR)?.textContent.trim() || 'Anonymous';
      const getBodyReview = element.querySelector(Selectors.REVIEW_BODY)?.textContent.trim();
      const getTitle = element.querySelector(Selectors.REVIEW_TITLE)?.textContent.trim();
      const ratingElement = element.querySelector(Selectors.REVIEW_RATING);

      if (!getDatePublished || !getBodyReview || !getTitle || !ratingElement) return null;

      const datePublished = formatDate(getDatePublished, 'yyyy-MM-dd');
      if (!datePublished) return null;

      const ratingValue = parseRatingScore(ratingElement);
      if (ratingValue == null) return null;

      return {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: getAuthorName,
        },
        datePublished,
        reviewBody: getBodyReview,
        name: getTitle,
        reviewRating: {
          '@type': 'Rating',
          bestRating: 5,
          ratingValue,
          worstRating: 1,
        },
      };
    };

    const aggregateRating = (widget) => {
      const rawCount = widget.dataset.numberOfReviews;
      const rawAvg = widget.dataset.averageRating;
      if (rawCount == null || rawCount === '' || rawAvg == null || rawAvg === '') {
        return null;
      }
      const reviewCount = parseInt(String(rawCount).trim(), 10);
      const ratingValue = parseFloat(String(rawAvg).replace(/,/g, '.'));
      if (
        !Number.isFinite(reviewCount)
        || reviewCount < 1
        || !Number.isFinite(ratingValue)
        || ratingValue < 1
        || ratingValue > 5
      ) {
        return null;
      }
      return {
        '@type': 'AggregateRating',
        ratingValue,
        reviewCount,
        bestRating: 5,
        worstRating: 1,
      };
    };

    const loadReviews = () => {
      if (!window.judgmeReviewData || !apiUrl || !apiToken || !shopDomain || !productHandle) {
        if (!isProduction) {
          console.error('Judge.me review config is missing required fields.', {
            hasApiUrl: !!apiUrl,
            hasApiToken: !!apiToken,
            hasShopDomain: !!shopDomain,
            hasProductHandle: !!productHandle,
          });
        }
        return;
      }
      fetchProductReviews().then((reviews) => {
        if (!reviews || !Array.isArray(reviews.review)) return;

        const validReviews = reviews.review.filter((r) => r !== null);
        if (validReviews.length === 0) return;

        const customMarkup = document.querySelector(Selectors.CUSTOM_MARKUP);
        if (!customMarkup || !customMarkup.textContent) return;

        try {
          const customMarkUpData = JSON.parse(customMarkup.textContent);
          const merged = { ...customMarkUpData, review: validReviews };
          if (reviews.aggregateRating) {
            merged.aggregateRating = reviews.aggregateRating;
          }
          customMarkup.textContent = JSON.stringify(merged, null, 2);
        } catch (e) {
          if (!isProduction) console.error('Failed to parse custom-markup JSON', e);
        }
      });
    };

    return {
      load: loadReviews,
    };
  })();

  JudgMeReviews.load();
});
